import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { CheckCircle2 } from "lucide-react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground border-border",
        // Trading-specific variants
        success:
          "border-transparent bg-success/15 text-success",
        danger:
          "border-transparent bg-destructive/15 text-destructive",
        warning:
          "border-transparent bg-warning/15 text-warning",
        // Status badges
        live:
          "border-transparent bg-success/15 text-success",
        completed:
          "border-transparent bg-secondary text-secondary-foreground",
        new:
          "border-transparent bg-primary/15 text-primary animate-pulse",
        hot:
          "border-transparent bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-400",
        // Special badges
        verified:
          "border-transparent bg-primary/15 text-primary",
        graduated:
          "border-transparent bg-gradient-to-r from-primary/20 to-success/20 text-primary",
        // Subtle variants
        "success-subtle":
          "border-success/20 bg-success/10 text-success",
        "danger-subtle":
          "border-destructive/20 bg-destructive/10 text-destructive",
        "primary-subtle":
          "border-primary/20 bg-primary/10 text-primary",
        // Glass variants
        glass:
          "border-border/50 bg-card/50 backdrop-blur-sm text-foreground",
        "glass-primary":
          "border-primary/30 bg-primary/10 backdrop-blur-sm text-primary",
      },
      size: {
        default: "px-2.5 py-0.5 text-xs",
        sm: "px-2 py-0.5 text-[10px]",
        lg: "px-3 py-1 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** Show a pulsing dot indicator */
  pulse?: boolean;
  /** Show verified checkmark icon */
  verified?: boolean;
}

function Badge({
  className,
  variant,
  size,
  pulse,
  verified,
  children,
  ...props
}: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {pulse && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-current" />
        </span>
      )}
      {verified && <CheckCircle2 className="h-3 w-3" />}
      {children}
    </div>
  );
}

// Convenience component for price change badges
interface PriceChangeBadgeProps extends Omit<BadgeProps, "variant" | "children"> {
  change: number;
  showIcon?: boolean;
}

function PriceChangeBadge({ change, showIcon = true, className, ...props }: PriceChangeBadgeProps) {
  const isPositive = change >= 0;
  const formattedChange = `${isPositive ? "+" : ""}${change.toFixed(2)}%`;

  return (
    <Badge
      variant={isPositive ? "success" : "danger"}
      className={cn("font-mono tabular-nums", className)}
      {...props}
    >
      {showIcon && (
        <span className="text-[10px]">{isPositive ? "▲" : "▼"}</span>
      )}
      {formattedChange}
    </Badge>
  );
}

// Convenience component for live status
function LiveBadge({ className, ...props }: Omit<BadgeProps, "variant" | "children">) {
  return (
    <Badge variant="live" pulse className={className} {...props}>
      LIVE
    </Badge>
  );
}

// Convenience component for completed status (no pulse, no dot, no animation)
function CompletedBadge({ className, ...props }: Omit<BadgeProps, "variant" | "children">) {
  return (
    <Badge variant="completed" className={className} {...props}>
      COMPLETED
    </Badge>
  );
}

// Convenience component for verified creators/tokens
function VerifiedBadge({ className, size = "sm", ...props }: Omit<BadgeProps, "variant" | "children">) {
  return (
    <Badge variant="verified" size={size} verified className={className} {...props}>
      Verified
    </Badge>
  );
}

export { Badge, badgeVariants, PriceChangeBadge, LiveBadge, CompletedBadge, VerifiedBadge };
